import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Euro, Home, Users, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import { useLanguage } from '../context/LanguageContext';

const Investeren = () => {
  const { t } = useLanguage();

  return (
    <div style={styles.page}>
      <SEO titleKey="seo.investTitle" descriptionKey="seo.investDescription" />
      <PageHero
        image="https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1920&q=80"
        title={t('invest.title')}
        subtitle={t('invest.subtitle')}
        testIdTitle="investeren-title"
      />

      <div style={styles.container}>
        {/* Intro */}
        <section style={styles.section}>
          <div style={styles.intro}>
            <p style={styles.introText}>{t('invest.intro')}</p>
          </div>
        </section>

        {/* Benefits */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t('invest.whyInvest')}</h2>
          <div style={styles.benefitsGrid}>
            <div style={styles.benefitCard}>
              <TrendingUp size={40} color="#C5A059" />
              <h3 style={styles.benefitTitle}>{t('invest.benefit1')}</h3>
              <p style={styles.benefitText}>{t('invest.benefit1Text')}</p>
            </div>
            <div style={styles.benefitCard}>
              <Euro size={40} color="#C5A059" />
              <h3 style={styles.benefitTitle}>{t('invest.benefit2')}</h3>
              <p style={styles.benefitText}>{t('invest.benefit2Text')}</p>
            </div>
            <div style={styles.benefitCard}>
              <Home size={40} color="#C5A059" />
              <h3 style={styles.benefitTitle}>{t('invest.benefit3')}</h3>
              <p style={styles.benefitText}>{t('invest.benefit3Text')}</p>
            </div>
            <div style={styles.benefitCard}>
              <Users size={40} color="#C5A059" />
              <h3 style={styles.benefitTitle}>{t('invest.benefit4')}</h3>
              <p style={styles.benefitText}>{t('invest.benefit4Text')}</p>
            </div>
          </div>
        </section>

        {/* ROI Example */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t('invest.example')}</h2>
          <div style={styles.roiCard}>
            <div style={styles.roiItem}>
              <span style={styles.roiLabel}>{t('invest.purchasePrice')}</span>
              <span style={styles.roiValue}>€350.000</span>
            </div>
            <div style={styles.roiItem}>
              <span style={styles.roiLabel}>{t('invest.annualRental')}</span>
              <span style={styles.roiValue}>€28.000</span>
            </div>
            <div style={styles.roiItem}>
              <span style={styles.roiLabel}>{t('invest.grossYield')}</span>
              <span style={styles.roiValue}>8%</span>
            </div>
            <div style={styles.roiItem}>
              <span style={styles.roiLabel}>{t('invest.appreciation')}</span>
              <span style={styles.roiValue}>€21.000</span>
            </div>
            <div style={styles.roiHighlight}>
              <span style={styles.roiLabel}>{t('invest.totalReturn')}</span>
              <span style={styles.roiValueLarge}>€49.000 (14%)</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={styles.ctaSection}>
          <h2 style={styles.sectionTitle}>{t('invest.readyToInvest')}</h2>
          <Link to="/woningen" style={styles.btnPrimary} data-testid="view-investment-properties-button">
            {t('invest.viewInvestmentProperties')}
            <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Link>
        </section>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    paddingBottom: '4rem',
  },
  header: {
    backgroundColor: '#E6DCCA',
    padding: '4rem 0',
    marginBottom: '4rem',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1.5rem',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
    color: '#1A1918',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  subtitle: {
    color: '#585755',
    fontSize: '1.1rem',
    textAlign: 'center',
  },
  section: {
    marginBottom: '6rem',
  },
  intro: {
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
  },
  introText: {
    color: '#585755',
    fontSize: '1.1rem',
    lineHeight: 1.8,
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2rem, 4vw, 2.5rem)',
    color: '#1A1918',
    marginBottom: '3rem',
    textAlign: 'center',
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '2rem',
  },
  benefitCard: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    padding: '2.5rem',
  },
  benefitTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem',
    color: '#1A1918',
    margin: '1.5rem 0 1rem',
  },
  benefitText: {
    color: '#585755',
    fontSize: '0.95rem',
    lineHeight: 1.7,
  },
  roiCard: {
    backgroundColor: 'white',
    border: '2px solid #EAE5DD',
    padding: '3rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  roiItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem 0',
    borderBottom: '1px solid #EAE5DD',
  },
  roiLabel: {
    color: '#585755',
    fontSize: '1rem',
  },
  roiValue: {
    color: '#1A1918',
    fontSize: '1.1rem',
    fontWeight: 500,
  },
  roiHighlight: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1.5rem 0 0',
    marginTop: '1rem',
  },
  roiValueLarge: {
    fontFamily: "'Playfair Display', serif",
    color: '#C5A059',
    fontSize: '1.75rem',
    fontWeight: 500,
  },
  ctaSection: {
    textAlign: 'center',
    backgroundColor: '#E6DCCA',
    padding: '4rem 2rem',
    margin: '0 -1.5rem',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '1rem 2rem',
    fontSize: '0.875rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: 500,
  },
};

export default Investeren;
